import { useEffect, useRef } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import { getOptionsFromRefList } from "@/util/common_function";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMAS } from "@/constants/schemas";
import { MODULES_LIST } from "@/util/common_variable";

const ExtraditionInformation = () => {
  const [form] = Form.useForm();
  const { activeWarrant, bookingsFieldsEnabled, patchArrayItem } = useActiveWarrant();

  // Debounce refs for text fields
  const debounceExtraditionLimits = useRef(null);
  const debounceReviewComments    = useRef(null);

  const toDayjs = (val) => {
    if (!val) return null;
    const d = dayjs(val);
    return d.isValid() ? d : null;
  };

  // Hydrate form only when the warrant itself changes
  useEffect(() => {
    if (!activeWarrant) return;

    const extradition = activeWarrant?.warrantExtraditionInfo?.[0];

    form.setFieldsValue({
      extraditionAreaID: extradition?.extraditionAreaID ?? undefined,
      extraditionLimits: extradition?.extraditionLimits ?? undefined,
      requiresReview:    extradition?.requiresReview    ?? false,
      reviewDate:        toDayjs(extradition?.reviewDate),
      reviewedByStaffID: extradition?.reviewedByStaffID ?? undefined,
      reviewComments:    extradition?.reviewComments    ?? "",
    });
  }, [activeWarrant?.warrantId]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      [debounceExtraditionLimits, debounceReviewComments]
        .forEach((r) => { if (r.current) clearTimeout(r.current); });
    };
  }, []);

  // Immediate patch for selects, checkboxes, dates
  const patchExtradition = (key, val) => {
    patchArrayItem("warrantExtraditionInfo", 0, { [key]: val });
  };

  // Debounced patch for text fields
  const makeDebouncedPatch = (timerRef, key) => (val) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      patchArrayItem("warrantExtraditionInfo", 0, { [key]: val });
    }, 300);
  };

  const patchExtraditionLimits = makeDebouncedPatch(debounceExtraditionLimits, "extraditionLimits");
  const patchReviewComments    = makeDebouncedPatch(debounceReviewComments,    "reviewComments");

  const requiresReview = Form.useWatch("requiresReview", form);

  return (
    <>
      <StyledSectionHeading marginTop="20px">
        <span>Extradition Information</span>
      </StyledSectionHeading>

      <FieldSecurityProvider
        entity={SCHEMAS.WARRANT_EXTRADITION_INFO.SCHEMA_NAME}
        startingPoint={MODULES_LIST.WARRANTS}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form form={form} disabled={!bookingsFieldsEnabled}>
          <StyledGridRow gutter={16}>
            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANT_EXTRADITION_INFO.FIELDS.EXTRADITION_AREA_ID}
                formItemProps={{ label: "Extradition Area", name: "extraditionAreaID" }}
              >
                <Select
                  options={getOptionsFromRefList("Extradition_Area", false, false, true)}
                  placeholder="Extradition Area"
                  variant="outlined"
                  onChange={(v) => patchExtradition("extraditionAreaID", v?.value ?? v)}
                  className="address-select--pink"
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANT_EXTRADITION_INFO.FIELDS.EXTRADITION_LIMITS}
                formItemProps={{ label: "Other Extradition Notes", name: "extraditionLimits" }}
              >
                <TextField
                  onChange={(e) => patchExtraditionLimits(e?.target?.value ?? e)}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANT_EXTRADITION_INFO.FIELDS.REQUIRES_REVIEW}
                formItemProps={{
                  label: "Requires review?",
                  name: "requiresReview",
                  labelPostion: "label-right",
                  layout: "horizontal",
                  paddingTop: "24px",
                  valuePropName: "checked",
                }}
              >
                <Checkbox
                  onChange={(e) => patchExtradition("requiresReview", e.target.checked)}
                />
              </SecureFormItem>
            </StyledGridCol>

            {requiresReview && (
              <>
                <StyledGridCol>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_EXTRADITION_INFO.FIELDS.REVIEW_DATE}
                    formItemProps={{ label: "Review Date", name: "reviewDate" }}
                  >
                    <CommonDatePicker
                      showTime={false}
                      format="MM/DD/YYYY"
                      onChange={(val) =>
                        patchExtradition("reviewDate", val ? dayjs(val).toISOString() : null)
                      }
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol lg={6}>
                  <SecureFormItem
                    fieldName={SCHEMAS.WARRANT_EXTRADITION_INFO.FIELDS.REVIEWED_BY_STAFF_ID}
                    formItemProps={{ label: "Reviewed By", name: "reviewedByStaffID" }}
                  >
                    <Select
                      options={getOptionsFromRefList("", true, true, true)}
                      placeholder="Reviewed By"
                      variant="outlined"
                      onChange={(v) => patchExtradition("reviewedByStaffID", v?.value ?? v)}
                    />
                  </SecureFormItem>
                </StyledGridCol>
              </>
            )}

            <StyledGridCol lg={12}>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANT_EXTRADITION_INFO.FIELDS.REVIEW_COMMENTS}
                formItemProps={{ label: "Review Comments", name: "reviewComments" }}
              >
                <TextField
                  onChange={(e) => patchReviewComments(e?.target?.value ?? e)}
                />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>
    </>
  );
};

export default ExtraditionInformation;
